import { Fragment } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  createColumnHelper,
} from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon, LockClosedIcon } from "@heroicons/react/24/outline";

import { ICheckInStep } from "../../models/CheckIn";

const colHelper = createColumnHelper<ICheckInStep>();

const cols = [
  colHelper.display({
    id: "name",
    header: "Name",
    sortingFn: "auto",
    cell: (info) => (
      <div className="ml-4">
        <div>{info.row.original.name}</div>
        {info.row.original.subtitle && (
          <div className="text-xs text-gray-500">{info.row.original.subtitle}</div>
        )}
      </div>
    )
  }),
  colHelper.display({
    id: "needsValue",
    header: "Needs Value",
    sortingFn: "auto",
    cell: (info) => (
      <span>{info.row.original.needsValue ? "Yes" : "No"}</span>
    )
  }),
  colHelper.display({
    id: "options",
    header: "Options",
    sortingFn: "auto",
    cell: (info) => (
      <span className="text-gray-600">{info.row.original.options || "-"}</span>
    )
  }),
  colHelper.display({
    id: "needsImage",
    header: "Needs Image",
    sortingFn: "auto",
    cell: (info) => (
      <span>{info.row.original.needsImage ? "Yes" : "No"}</span>
    )
  }),
  colHelper.display({
    id: "isSensitive",
    header: "Personal Data",
    sortingFn: "auto",
    cell: (info) => (
      info.row.original.isSensitive ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          <LockClosedIcon className="h-3 w-3" />
          Protected
        </span>
      ) : (
        <span className="text-gray-400">-</span>
      )
    )
  }),
  colHelper.display({
    id: "order",
    header: "Order",
    sortingFn: "auto",
    cell: (info) => (
      <span>{info.row.original.order}</span>
    )
  }),
  colHelper.display({
    id: "isActive",
    header: "Active",
    sortingFn: "auto",
    cell: (info) => (
      <span className={info.row.original.isActive ? "text-green-700" : "text-gray-500"}>
        {info.row.original.isActive ? "Active" : "Inactive"}
      </span>
    )
  }),
];

export default function CheckInStepsTable({
  checkInSteps,
  setCheckInStep,
  moveCheckInStep,
  isReordering,
}: {
  checkInSteps: ICheckInStep[],
  setCheckInStep: (checkInStep: ICheckInStep) => void,
  moveCheckInStep: (checkInStepId: string, direction: "up" | "down") => void,
  isReordering: boolean,
}) {
  const table = useReactTable({
    data: checkInSteps,
    columns: cols,
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="mt-4 bg-white overflow-auto">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                  </th>
                ))}
                <th
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  Move
                </th>
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    onClick={() => setCheckInStep(row.original)}
                    className="hover:bg-gray-100 hover:cursor-pointer"
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 py-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td className="px-3 py-3 border text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveCheckInStep(row.original.id, "up");
                          }}
                          disabled={isReordering || row.index === 0}
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveCheckInStep(row.original.id, "down");
                          }}
                          disabled={isReordering || row.index === checkInSteps.length - 1}
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {row.getIsExpanded() && (
                    <></>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
